/**
 * Probe a large import for coercion work already stored on its invalid rows.
 * Mirrors `LargeImportCoercionReview`'s own fetch — one bounded request per file
 * over the same `MAX_COERCION_ROWS` working set the agent writes to — so it sees
 * every row that could carry a prior proposal or saved edit, not just the first
 * cross-file page. Treating a single page as conclusive missed stored work on
 * later rows (or in a second file) and re-ran the agent needlessly. Returns true
 * if any invalid row already has proposals or saved coercion edits.
 *
 * Shared by `ReviewModal` and `ReviewStep` — both large-import coercion entry
 * points must resume identically instead of each maintaining its own copy.
 */

import { getCsvImportRows } from '@/lib/csv-import/api';

import { MAX_COERCION_ROWS } from './constants';

// Thrown when a per-file probe request fails, so callers can log which file's
// request failed rather than just "the probe failed" with no file context.
export interface ProbeRequestError extends Error {
  fileId?: string;
}

export async function hasStoredLargeImportCoercions(
  orgId: number,
  importId: number,
  fileIds: string[]
): Promise<boolean> {
  const pages = await Promise.all(
    fileIds.map(fileId =>
      getCsvImportRows(orgId, importId, {
        action: 'invalid',
        fileId,
        limit: MAX_COERCION_ROWS,
        offset: 0,
      }).catch((err: unknown) => {
        if (err instanceof Error) {
          (err as ProbeRequestError).fileId = fileId;
        }
        throw err;
      })
    )
  );
  return pages.some(page =>
    page.results.some(
      row =>
        (row.proposals && Object.keys(row.proposals).length > 0) ||
        (row.coercion_edits && Object.keys(row.coercion_edits).length > 0)
    )
  );
}
